import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveTemplate,
  listTemplates,
  type OutboundTemplate,
  type TemplateStatus,
} from '../api/templates.js';
import { listConnectors } from '../api/connectors.js';
import { ApiError } from '../api/apiClient.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';
import { SyncProviderTemplatesModal } from './SyncProviderTemplatesModal.js';

const STATUS_TONE: Record<TemplateStatus, 'green' | 'red' | 'slate' | 'yellow'> = {
  approved: 'green',
  pending: 'yellow',
  rejected: 'red',
  draft: 'slate',
};

export function TemplatesList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [connectorFilter, setConnectorFilter] = useState<string>('');
  const [confirmTarget, setConfirmTarget] = useState<OutboundTemplate | null>(null);
  const pushToast = useUiStore((s) => s.pushToast);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);

  const connectorsQuery = useQuery({
    queryKey: ['connectors', id, { includeArchived: false }],
    queryFn: () => listConnectors(id),
    enabled: Boolean(id),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['templates', id, { includeArchived, connectorId: connectorFilter || undefined }],
    queryFn: () =>
      listTemplates(id, {
        includeArchived,
        connectorId: connectorFilter || undefined,
      }),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: (templateId: string) => archiveTemplate(id, templateId),
    onSuccess: () => {
      pushToast('success', t.template.archived);
      queryClient.invalidateQueries({ queryKey: ['templates', id] });
      setConfirmTarget(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const templates = data ?? [];
  const connectors = connectorsQuery.data ?? [];
  const connectorById = new Map(connectors.map((c) => [c.id, c]));

  // #161 — Sync modal state. Solo habilitado si hay connector filtrado y soporta sync.
  const [syncOpen, setSyncOpen] = useState(false);
  const syncableConnector =
    connectorFilter && (connectorById.get(connectorFilter)?.provider === 'meta_native' ||
      connectorById.get(connectorFilter)?.provider === '360dialog')
      ? connectorFilter
      : '';

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.template.listTitle}</h2>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">
            <span className="sr-only">{t.template.filterConnector}</span>
            <select
              aria-label={t.template.filterConnector}
              value={connectorFilter}
              onChange={(e) => setConnectorFilter(e.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            >
              <option value="">{t.template.filterAll}</option>
              {connectors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t.template.showArchived}
          </label>
          {/* #161 — Sincronizar desde provider (requiere connector Meta/360dialog filtrado). */}
          <button
            type="button"
            onClick={() => setSyncOpen(true)}
            disabled={!syncableConnector}
            title={
              syncableConnector
                ? 'Listar templates remotos del provider e importar'
                : 'Filtrá por un connector Meta o 360dialog para sincronizar'
            }
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Sincronizar desde provider
          </button>
          <Link
            to={`/tenants/${id}/templates/new`}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
          >
            {t.template.newBtn}
          </Link>
        </div>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && templates.length === 0 && (
        <p className="text-slate-500">{t.template.empty}</p>
      )}

      {templates.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">{t.template.fields.name}</th>
                <th className="px-4 py-2">{t.template.fields.templateName}</th>
                <th className="px-4 py-2">{t.template.fields.connector}</th>
                <th className="px-4 py-2">{t.template.fields.language}</th>
                <th className="px-4 py-2">{t.template.fields.category}</th>
                <th className="px-4 py-2">{t.template.fields.status}</th>
                <th className="px-4 py-2">{t.template.fields.version}</th>
                <th className="px-4 py-2 text-right">{t.template.fields.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {templates.map((tpl) => {
                const conn = connectorById.get(tpl.connectorId);
                return (
                  <tr
                    key={tpl.id}
                    data-testid={`template-row-${tpl.id}`}
                    className={tpl.deletedAt ? 'opacity-50' : undefined}
                  >
                    <td className="px-4 py-2 font-medium">{tpl.name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{tpl.templateName}</td>
                    <td className="px-4 py-2 text-slate-600">{conn?.name ?? tpl.connectorId}</td>
                    <td className="px-4 py-2 font-mono text-xs">{tpl.language}</td>
                    <td className="px-4 py-2 text-xs">{tpl.category}</td>
                    <td className="px-4 py-2">
                      <StatusBadge
                        label={t.template.status[tpl.status]}
                        tone={STATUS_TONE[tpl.status]}
                      />
                    </td>
                    <td className="px-4 py-2 text-slate-500">v{tpl.version}</td>
                    <td className="px-4 py-2 text-right">
                      {!tpl.deletedAt && (
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/tenants/${id}/templates/${tpl.id}/edit`}
                            className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                          >
                            {t.template.edit}
                          </Link>
                          <button
                            type="button"
                            onClick={() => setConfirmTarget(tpl)}
                            className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          >
                            {t.template.archive}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        title={t.template.confirmArchiveTitle}
        description={t.template.confirmArchiveDesc}
        confirmLabel={t.template.archive}
        cancelLabel={t.app.cancel}
        loading={archiveMutation.isPending}
        onConfirm={() => {
          if (confirmTarget) archiveMutation.mutate(confirmTarget.id);
        }}
        onCancel={() => setConfirmTarget(null)}
      />

      {syncOpen && syncableConnector && (
        <SyncProviderTemplatesModal
          tenantId={id}
          connectorId={syncableConnector}
          localTemplates={templates}
          onClose={() => setSyncOpen(false)}
          onImported={() => {
            queryClient.invalidateQueries({ queryKey: ['templates', id] });
          }}
        />
      )}
    </div>
  );
}
